/* eslint-disable */
/**
 * GENERATED FILE — DO NOT EDIT.
 *
 * Generated from backend/shared/utils/richText.js by scripts/sync-rich-text.js.
 * Edit the backend module and re-run:  node scripts/sync-rich-text.js
 *
 * The tenant portal's description format toggle must produce byte-identical
 * output to the API, so both run the same code.
 */

/**
 * Rich-text description conversion — dependency-free.
 *
 * Item descriptions can be authored as plain text, Markdown, HTML, or RTF, and
 * the API can emit any of those on request (tenants pipe descriptions into
 * third-party systems with differing requirements).
 *
 * ── Why no third-party library ────────────────────────────────────────────────
 * The obvious picks don't hold up as production dependencies here:
 *   - turndown (HTML->Markdown) is no longer actively maintained.
 *   - html-to-rtf (the only JS HTML->RTF generator) last shipped in 2022 and
 *     pins cheerio 1.0.0-rc.1 (a 2016 release candidate) + juice 4 (stable 8).
 *     It would not survive `npm audit --omit=dev --audit-level=high`.
 *   - @jonahschulte/rtf-toolkit published 5 versions across 3 days in Dec 2025
 *     and nothing since; despite its "html-to-rtf" keyword it only parses
 *     RTF -> HTML, not the direction we actually need.
 *   - officeparser / rtf-stream-parser are RTF readers, not writers.
 *   - unified/remark/rehype IS well maintained, but it is ESM-only while these
 *     Lambdas are CommonJS, and adds ~10 transitive packages to every bundle.
 * This mirrors `reportFormats.js`, which hand-builds XLSX and PDF for the same
 * reasons. A description needs a small, well-defined subset of formatting (see
 * SUPPORTED), which is very tractable to convert exactly.
 *
 * ── Model ─────────────────────────────────────────────────────────────────────
 * Every format parses into one document AST, and every format is emitted from
 * that AST — N parsers + N emitters rather than N^2 converters, so adding a
 * format later cannot silently skip a conversion pair.
 *
 * Blocks : paragraph | heading | list | blockquote | codeBlock | thematicBreak
 * Inline : text | strong | em | underline | strike | code | link | break
 *
 * The AST is deliberately the *intersection* of what all four formats express.
 * Anything outside it degrades predictably and is documented in LIMITATIONS,
 * which the tenant portal surfaces in an info popup.
 */

const FORMATS = ['plain', 'markdown', 'html', 'rtf'];
const DEFAULT_FORMAT = 'plain';

// ─── Security: HTML allowlist ────────────────────────────────────────────────
// Descriptions authored by one tenant's users are rendered in the tenant portal
// AND the platform admin portal, so unsanitized HTML is a stored-XSS path across
// a privilege boundary. Parsing through the AST *is* the sanitizer: only these
// tags produce nodes, everything else is dropped (its text is kept), and no
// attribute survives except a scheme-checked href.
const ALLOWED_TAGS = new Set([
  'p', 'br', 'div', 'span',
  'b', 'strong', 'i', 'em', 'u', 'ins', 's', 'strike', 'del',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code',
  'a', 'hr',
]);

// Content inside these is discarded wholesale rather than unwrapped to text —
// otherwise `<script>alert(1)</script>` would surface as visible text.
const DROP_CONTENT_TAGS = new Set(['script', 'style', 'iframe', 'object', 'embed', 'template', 'noscript', 'svg', 'math']);

const BLOCK_TAGS = new Set(['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'blockquote', 'pre', 'hr', 'li']);
const VOID_TAGS = new Set(['br', 'hr', 'img', 'input', 'meta', 'link', 'source']);

// Only these URL schemes may appear in an href.
const SAFE_URL = /^(https?:|mailto:|tel:|#|\/)/i;

/** Documented lossy conversions, surfaced to users in the UI info popup. */
const LIMITATIONS = [
  { from: 'html', to: 'markdown', note: 'Inline styles, colours, font sizes, CSS classes, tables, and images are dropped. Bold, italic, underline, strikethrough, headings, lists, quotes, code, and links survive.' },
  { from: 'html', to: 'plain', note: 'All formatting is removed. Block structure becomes blank lines; list items keep a dash or number prefix.' },
  { from: 'html', to: 'rtf', note: 'Links become the link text followed by the URL in angle brackets. Tables and images are not represented.' },
  { from: 'markdown', to: 'html', note: 'Raw HTML embedded in Markdown is escaped rather than passed through, so it renders as visible text. The one exception is <u>, which is preserved because Markdown has no underline syntax of its own.' },
  { from: 'markdown', to: 'plain', note: 'Syntax markers are removed; the words remain in reading order.' },
  { from: 'rtf', to: 'any', note: 'RTF is read for a documented subset: bold, italic, underline, strikethrough, paragraphs, and Unicode escapes. Font tables, colours, tab stops, embedded images, and tracked changes are ignored — their text is kept, their styling is not.' },
  { from: 'plain', to: 'any', note: 'Plain text carries no formatting, so nothing is lost and nothing is gained. Blank lines become paragraph breaks.' },
  { from: 'any', to: 'any', note: 'Underline has no native Markdown equivalent and is emitted as a <u> tag. Nested lists are flattened to a single level.' },
];

const SUPPORTED = [
  'bold', 'italic', 'underline', 'strikethrough', 'inline code', 'links',
  'headings (1-6)', 'paragraphs', 'line breaks', 'bulleted lists',
  'numbered lists', 'block quotes', 'code blocks', 'horizontal rules',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const escapeHtml = (s) => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const NAMED_ENTITIES = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', tab: '\t',
  newline: '\n', mdash: '\u2014', ndash: '\u2013', hellip: '\u2026',
  copy: '\u00a9', reg: '\u00ae', trade: '\u2122', deg: '\u00b0',
  ldquo: '\u201c', rdquo: '\u201d', lsquo: '\u2018', rsquo: '\u2019',
  middot: '\u00b7', bull: '\u2022', laquo: '\u00ab', raquo: '\u00bb',
};

function decodeEntities(s) {
  return String(s).replace(/&(#[xX]?[0-9a-fA-F]+|[a-zA-Z]+);?/g, (match, body) => {
    if (body[0] === '#') {
      const hex = body[1] === 'x' || body[1] === 'X';
      const code = parseInt(hex ? body.slice(2) : body.slice(1), hex ? 16 : 10);
      if (!Number.isFinite(code) || code < 1 || code > 0x10ffff) { return match; }
      try { return String.fromCodePoint(code); } catch { return match; }
    }
    const named = NAMED_ENTITIES[body.toLowerCase()];
    return named === undefined ? match : named;
  });
}

function safeHref(url) {
  // Decode entities first — `&#106;avascript:` and `&Tab;` are both used to
  // hide the scheme from naive checks. Then strip control/whitespace chars,
  // since browsers read "java\tscript:" as the javascript: scheme.
  const decoded = decodeEntities(String(url || '')).trim();
  // eslint-disable-next-line no-control-regex
  const collapsed = decoded.replace(/[\u0000-\u0020\u007f-\u00a0]/g, '');
  return SAFE_URL.test(collapsed) ? collapsed : null;
}

// ─── AST helpers ─────────────────────────────────────────────────────────────

const MARK_TAGS = {
  b: 'strong', strong: 'strong', i: 'em', em: 'em',
  u: 'underline', ins: 'underline',
  s: 'strike', strike: 'strike', del: 'strike',
};

function inlineText(nodes) {
  return (nodes || []).map((n) => {
    if (n.type === 'text' || n.type === 'code') { return n.value; }
    if (n.type === 'break') { return '\n'; }
    return inlineText(n.children);
  }).join('');
}

/** Collapse adjacent text nodes and drop empties — keeps output stable. */
function normalizeInline(nodes) {
  const out = [];
  for (const node of nodes || []) {
    if (!node) { continue; }
    if (node.children) {
      node.children = normalizeInline(node.children);
      // A mark with no content contributes nothing.
      if (node.children.length === 0) { continue; }
    }
    if (node.type === 'text') {
      if (node.value === '') { continue; }
      const prev = out[out.length - 1];
      if (prev && prev.type === 'text') { prev.value += node.value; continue; }
    }
    out.push(node);
  }
  return out;
}

/** Drop blocks carrying no content (empty paragraphs from stray markup). */
function normalizeBlocks(blocks) {
  const out = [];
  for (const b of blocks || []) {
    if (!b) { continue; }
    if (b.type === 'thematicBreak') { out.push(b); continue; }
    if (b.type === 'codeBlock') {
      if (b.value !== '') { out.push(b); }
      continue;
    }
    if (b.type === 'list') {
      b.items = b.items.map((i) => normalizeBlocks(i)).filter((i) => i.length > 0);
      if (b.items.length > 0) { out.push(b); }
      continue;
    }
    if (b.type === 'blockquote') {
      b.blocks = normalizeBlocks(b.blocks);
      if (b.blocks.length > 0) { out.push(b); }
      continue;
    }
    b.children = normalizeInline(b.children);
    if (b.children.length > 0 && inlineText(b.children).trim() !== '') { out.push(b); }
  }
  return out;
}

// ─── Parser: plain text ──────────────────────────────────────────────────────
// A blank line separates paragraphs; a single newline is a hard line break.

function parsePlain(input) {
  const src = String(input || '').replace(/\r\n?/g, '\n');
  if (src.trim() === '') { return []; }
  return src.split(/\n{2,}/).map((chunk) => {
    const children = [];
    chunk.split('\n').forEach((line, i) => {
      if (i > 0) { children.push({ type: 'break' }); }
      children.push({ type: 'text', value: line });
    });
    return { type: 'paragraph', children };
  }).filter((p) => inlineText(p.children).trim() !== '');
}

// ─── Parser: Markdown ────────────────────────────────────────────────────────

const MD_INLINE = [
  // Order matters: ** before *, ~~ before ~, and code spans win outright.
  { re: /^`([^`\n]+)`/, make: (m) => ({ type: 'code', value: m[1] }) },
  { re: /^\*\*([\s\S]+?)\*\*/, make: (m) => ({ type: 'strong', children: parseMdInline(m[1]) }) },
  { re: /^__([\s\S]+?)__/, make: (m) => ({ type: 'strong', children: parseMdInline(m[1]) }) },
  { re: /^~~([\s\S]+?)~~/, make: (m) => ({ type: 'strike', children: parseMdInline(m[1]) }) },
  { re: /^\*([^*\n]+)\*/, make: (m) => ({ type: 'em', children: parseMdInline(m[1]) }) },
  { re: /^_([^_\n]+)_/, make: (m) => ({ type: 'em', children: parseMdInline(m[1]) }) },
  // Markdown has no underline syntax, so our emitter writes <u>. Recognising it
  // here is what makes markdown -> markdown round-trip underline correctly.
  { re: /^<u>([\s\S]*?)<\/u>/i, make: (m) => ({ type: 'underline', children: parseMdInline(m[1]) }) },
  { re: /^\[([^\]]*)\]\(([^)\s]*)\)/, make: (m) => {
    const href = safeHref(m[2]);
    const children = parseMdInline(m[1]);
    return href ? { type: 'link', href, children } : { type: 'text', value: m[1] };
  } },
  { re: /^<((?:https?:\/\/|mailto:)[^>\s]+)>/, make: (m) => ({ type: 'link', href: safeHref(m[1]), children: [{ type: 'text', value: m[1] }] }) },
];

function parseMdInline(src) {
  const out = [];
  let rest = String(src);
  let buffer = '';
  const flush = () => { if (buffer) { out.push({ type: 'text', value: buffer }); buffer = ''; } };

  while (rest.length > 0) {
    // Backslash escape: the next character is literal.
    if (rest[0] === '\\' && rest.length > 1) { buffer += rest[1]; rest = rest.slice(2); continue; }
    let matched = false;
    for (const rule of MD_INLINE) {
      const m = rest.match(rule.re);
      if (m) {
        const node = rule.make(m);
        if (node) { flush(); out.push(node); }
        rest = rest.slice(m[0].length);
        matched = true;
        break;
      }
    }
    if (matched) { continue; }
    buffer += rest[0];
    rest = rest.slice(1);
  }
  flush();
  return normalizeInline(out);
}

function parseMarkdown(input) {
  const lines = String(input || '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let paragraph = [];
  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ type: 'paragraph', children: parseMdInline(paragraph.join('\n')) });
      paragraph = [];
    }
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    // Fenced code block.
    const fence = line.match(/^\s*(```+|~~~+)\s*(\S*)\s*$/);
    if (fence) {
      flushParagraph();
      const marker = fence[1][0];
      const body = [];
      i += 1;
      while (i < lines.length && !new RegExp(`^\\s*${marker === '`' ? '```' : '~~~'}+\\s*$`).test(lines[i])) {
        body.push(lines[i]); i += 1;
      }
      blocks.push({ type: 'codeBlock', lang: fence[2] || null, value: body.join('\n') });
      continue;
    }
    if (line.trim() === '') { flushParagraph(); continue; }
    // Setext heading (text underlined with === or ---). Checked before the
    // thematic-break rule because `---` under a paragraph is a heading, not a
    // rule — same precedence CommonMark uses.
    if (paragraph.length > 0 && /^\s{0,3}(=+|-+)\s*$/.test(line)) {
      blocks.push({
        type: 'heading',
        level: line.trim()[0] === '=' ? 1 : 2,
        children: parseMdInline(paragraph.join('\n')),
      });
      paragraph = [];
      continue;
    }
    if (/^\s{0,3}(\*\s*){3,}$|^\s{0,3}(-\s*){3,}$|^\s{0,3}(_\s*){3,}$/.test(line)) {
      flushParagraph(); blocks.push({ type: 'thematicBreak' }); continue;
    }
    const heading = line.match(/^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/);
    if (heading) {
      flushParagraph();
      blocks.push({ type: 'heading', level: heading[1].length, children: parseMdInline(heading[2]) });
      continue;
    }
    const quote = line.match(/^\s{0,3}>\s?(.*)$/);
    if (quote) {
      flushParagraph();
      const body = [quote[1]];
      while (i + 1 < lines.length && /^\s{0,3}>\s?/.test(lines[i + 1])) {
        body.push(lines[i + 1].replace(/^\s{0,3}>\s?/, '')); i += 1;
      }
      blocks.push({ type: 'blockquote', blocks: parseMarkdown(body.join('\n')) });
      continue;
    }
    const bullet = line.match(/^\s{0,8}[-*+]\s+(.*)$/);
    const numbered = line.match(/^\s{0,8}(\d+)[.)]\s+(.*)$/);
    if (bullet || numbered) {
      flushParagraph();
      const ordered = Boolean(numbered);
      const items = [];
      let j = i;
      for (; j < lines.length; j += 1) {
        const b = lines[j].match(/^\s{0,8}[-*+]\s+(.*)$/);
        const n = lines[j].match(/^\s{0,8}(\d+)[.)]\s+(.*)$/);
        if (ordered && n) { items.push(n[2]); } else if (!ordered && b) { items.push(b[1]); } else { break; }
      }
      i = j - 1;
      blocks.push({
        type: 'list',
        ordered,
        items: items.map((t) => [{ type: 'paragraph', children: parseMdInline(t) }]),
      });
      continue;
    }
    paragraph.push(line);
  }
  flushParagraph();
  return normalizeBlocks(blocks);
}

// ─── Parser: HTML ────────────────────────────────────────────────────────────

/** Tokenize into a lightweight tree, applying the tag allowlist as we go. */
function parseHtmlTree(html) {
  const root = { tag: '#root', children: [] };
  const stack = [root];
  const src = String(html || '');
  // Comments, tags, or a run of text.
  const token = /<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?\]\]>|<!\w[^>]*>|<\/\s*([a-zA-Z][\w:-]*)\s*>|<([a-zA-Z][\w:-]*)((?:"[^"]*"|'[^']*'|[^>"'])*?)(\/?)>|[^<]+|</g;

  let match;
  let skipUntil = null; // when set, discard everything until this closing tag
  while ((match = token.exec(src)) !== null) {
    const [raw, closeTag, openTag, attrs, selfClose] = match;

    if (skipUntil) {
      if (closeTag && closeTag.toLowerCase() === skipUntil) { skipUntil = null; }
      continue;
    }
    if (raw.startsWith('<!--') || raw.startsWith('<![CDATA[') || /^<!\w/.test(raw)) { continue; }

    if (openTag) {
      const tag = openTag.toLowerCase();
      if (DROP_CONTENT_TAGS.has(tag)) {
        if (!selfClose) { skipUntil = tag; }
        continue;
      }
      if (!ALLOWED_TAGS.has(tag)) { continue; } // unwrap: children join the parent
      const node = { tag, children: [] };
      if (tag === 'a') {
        const href = (attrs || '').match(/\bhref\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i);
        node.href = href ? safeHref(href[2] ?? href[3] ?? href[4]) : null;
      }
      // Implicit close: <li>a<li>b and <p>a<p>b.
      const top = stack[stack.length - 1];
      if ((tag === 'li' && top.tag === 'li') || (tag === 'p' && top.tag === 'p')) { stack.pop(); }
      stack[stack.length - 1].children.push(node);
      if (!VOID_TAGS.has(tag) && !selfClose) { stack.push(node); }
      continue;
    }
    if (closeTag) {
      const tag = closeTag.toLowerCase();
      // Unwind to the matching open tag; ignore strays.
      for (let d = stack.length - 1; d > 0; d -= 1) {
        if (stack[d].tag === tag) { stack.length = d; break; }
      }
      continue;
    }
    if (raw.trim() !== '' || raw.includes(' ')) {
      stack[stack.length - 1].children.push({ tag: '#text', value: decodeEntities(raw) });
    }
  }
  return root;
}

function htmlInline(nodes) {
  const out = [];
  for (const node of nodes || []) {
    if (node.tag === '#text') { out.push({ type: 'text', value: node.value }); continue; }
    if (node.tag === 'br') { out.push({ type: 'break' }); continue; }
    const mark = MARK_TAGS[node.tag];
    if (mark) { out.push({ type: mark, children: htmlInline(node.children) }); continue; }
    if (node.tag === 'code') { out.push({ type: 'code', value: nodeText(node) }); continue; }
    if (node.tag === 'a') {
      const children = htmlInline(node.children);
      out.push(node.href ? { type: 'link', href: node.href, children } : { type: 'text', value: inlineText(children) });
      continue;
    }
    // span/div/anything else: unwrap, keeping the text.
    out.push(...htmlInline(node.children));
  }
  return normalizeInline(out);
}

function nodeText(node) {
  if (node.tag === '#text') { return node.value; }
  if (node.tag === 'br') { return '\n'; }
  return (node.children || []).map(nodeText).join('');
}

function htmlBlocks(nodes) {
  const blocks = [];
  let pending = [];
  const flush = () => {
    if (pending.length > 0) {
      const children = normalizeInline(pending);
      if (children.length > 0) { blocks.push({ type: 'paragraph', children }); }
      pending = [];
    }
  };

  for (const node of nodes || []) {
    if (node.tag === '#text') {
      if (node.value.trim() !== '') { pending.push({ type: 'text', value: node.value }); }
      continue;
    }
    if (!BLOCK_TAGS.has(node.tag)) { pending.push(...htmlInline([node])); continue; }

    flush();
    const tag = node.tag;
    if (tag === 'hr') { blocks.push({ type: 'thematicBreak' }); continue; }
    if (/^h[1-6]$/.test(tag)) {
      blocks.push({ type: 'heading', level: Number(tag[1]), children: htmlInline(node.children) });
      continue;
    }
    if (tag === 'ul' || tag === 'ol') {
      const items = (node.children || [])
        .filter((c) => c.tag === 'li')
        .map((li) => htmlBlocks(li.children));
      blocks.push({ type: 'list', ordered: tag === 'ol', items });
      continue;
    }
    if (tag === 'blockquote') { blocks.push({ type: 'blockquote', blocks: htmlBlocks(node.children) }); continue; }
    if (tag === 'pre') { blocks.push({ type: 'codeBlock', lang: null, value: nodeText(node).replace(/\n$/, '') }); continue; }
    if (tag === 'li') { blocks.push(...htmlBlocks(node.children)); continue; }
    // p / div — a div may itself contain blocks, so recurse and inline the result.
    const inner = htmlBlocks(node.children);
    blocks.push(...(inner.length > 0 ? inner : []));
  }
  flush();
  return blocks;
}

function parseHtml(input) {
  return normalizeBlocks(htmlBlocks(parseHtmlTree(input).children));
}

// ─── Parser: RTF (documented subset) ─────────────────────────────────────────
// Destinations we skip entirely — their content is metadata, not body text.
const RTF_SKIP_DESTINATIONS = new Set([
  'fonttbl', 'colortbl', 'stylesheet', 'info', 'revtbl', 'listtable',
  'listoverridetable', 'pict', 'object', 'themedata', 'colorschememapping',
  'latentstyles', 'datastore', 'generator', 'xmlnstbl', 'filetbl',
]);

function parseRtf(input) {
  const src = String(input || '');
  const blocks = [];
  let children = [];
  const style = { strong: false, em: false, underline: false, strike: false };
  let buffer = '';
  let i = 0;
  const depthStack = [];
  let skipDepth = -1;

  const flushText = () => {
    if (buffer === '') { return; }
    let node = { type: 'text', value: buffer };
    // Wrap innermost-out so the nesting order is stable and testable.
    if (style.strike) { node = { type: 'strike', children: [node] }; }
    if (style.underline) { node = { type: 'underline', children: [node] }; }
    if (style.em) { node = { type: 'em', children: [node] }; }
    if (style.strong) { node = { type: 'strong', children: [node] }; }
    children.push(node);
    buffer = '';
  };
  const endParagraph = () => {
    flushText();
    if (children.length > 0) { blocks.push({ type: 'paragraph', children }); }
    children = [];
  };

  while (i < src.length) {
    const ch = src[i];

    if (ch === '{') { flushText(); depthStack.push({ ...style }); i += 1; continue; }
    if (ch === '}') {
      // Flush BEFORE restoring: text buffered inside the group was written
      // under the group's formatting, not the parent's. Restoring first would
      // silently drop the formatting of every `{\b bold}` run.
      flushText();
      const restored = depthStack.pop();
      if (restored) { Object.assign(style, restored); }
      if (skipDepth >= 0 && depthStack.length < skipDepth) { skipDepth = -1; }
      i += 1;
      continue;
    }
    if (skipDepth >= 0) { i += 1; continue; }

    if (ch === '\\') {
      const next = src[i + 1];
      // Escaped literals.
      if (next === '\\' || next === '{' || next === '}') { buffer += next; i += 2; continue; }
      // Hex escape \'hh
      if (next === "'") {
        const hex = src.substr(i + 2, 2);
        const code = parseInt(hex, 16);
        if (Number.isFinite(code)) { buffer += String.fromCharCode(code); }
        i += 4;
        continue;
      }
      // Ignored-destination marker {\*\foo ...}
      if (next === '*') { skipDepth = depthStack.length; i += 2; continue; }
      if (next === '\n' || next === '\r') { flushText(); children.push({ type: 'break' }); i += 2; continue; }

      const cw = src.slice(i + 1).match(/^([a-zA-Z]+)(-?\d+)?[ ]?/);
      if (!cw) { i += 1; continue; }
      const word = cw[1];
      const param = cw[2] === undefined ? null : parseInt(cw[2], 10);
      i += 1 + cw[0].length;

      if (RTF_SKIP_DESTINATIONS.has(word)) { skipDepth = depthStack.length; continue; }
      switch (word) {
        case 'par': case 'pard': if (word === 'par') { endParagraph(); } break;
        case 'line': flushText(); children.push({ type: 'break' }); break;
        case 'tab': buffer += '\t'; break;
        case 'b': flushText(); style.strong = param !== 0; break;
        case 'i': flushText(); style.em = param !== 0; break;
        case 'ul': flushText(); style.underline = param !== 0; break;
        case 'ulnone': flushText(); style.underline = false; break;
        case 'strike': flushText(); style.strike = param !== 0; break;
        case 'plain':
          flushText();
          style.strong = false; style.em = false; style.underline = false; style.strike = false;
          break;
        case 'u': {
          // \uN with a signed 16-bit code point, followed by a fallback char.
          if (param !== null) {
            const code = param < 0 ? param + 65536 : param;
            try { buffer += String.fromCodePoint(code); } catch { /* skip bad code */ }
          }
          // Skip the substitution character that follows (\uc1 default).
          if (src[i] === '?') { i += 1; }
          break;
        }
        default: break; // Unknown control word: ignored, its text is kept.
      }
      continue;
    }
    if (ch === '\n' || ch === '\r') { i += 1; continue; }
    buffer += ch;
    i += 1;
  }
  endParagraph();
  return normalizeBlocks(blocks);
}

// ─── Emitter: HTML ───────────────────────────────────────────────────────────

function htmlInlineOut(nodes) {
  return (nodes || []).map((n) => {
    switch (n.type) {
      case 'text': return escapeHtml(n.value);
      case 'break': return '<br />';
      case 'code': return `<code>${escapeHtml(n.value)}</code>`;
      case 'strong': return `<strong>${htmlInlineOut(n.children)}</strong>`;
      case 'em': return `<em>${htmlInlineOut(n.children)}</em>`;
      case 'underline': return `<u>${htmlInlineOut(n.children)}</u>`;
      case 'strike': return `<s>${htmlInlineOut(n.children)}</s>`;
      case 'link': return `<a href="${escapeHtml(n.href)}" rel="noopener noreferrer">${htmlInlineOut(n.children)}</a>`;
      default: return '';
    }
  }).join('');
}

function toHtml(blocks) {
  return (blocks || []).map((b) => {
    switch (b.type) {
      case 'heading': return `<h${b.level}>${htmlInlineOut(b.children)}</h${b.level}>`;
      case 'thematicBreak': return '<hr />';
      case 'codeBlock': return `<pre><code>${escapeHtml(b.value)}</code></pre>`;
      case 'blockquote': return `<blockquote>${toHtml(b.blocks)}</blockquote>`;
      case 'list': {
        const tag = b.ordered ? 'ol' : 'ul';
        const items = b.items.map((item) => {
          // A single paragraph in a list item renders inline, not wrapped.
          const inner = item.length === 1 && item[0].type === 'paragraph'
            ? htmlInlineOut(item[0].children)
            : toHtml(item);
          return `<li>${inner}</li>`;
        }).join('');
        return `<${tag}>${items}</${tag}>`;
      }
      default: return `<p>${htmlInlineOut(b.children)}</p>`;
    }
  }).join('\n');
}

// ─── Emitter: Markdown ───────────────────────────────────────────────────────

/** Escape characters that would otherwise be read as Markdown syntax. */
const escapeMd = (s) => String(s).replace(/([\\`*_[\]()#+\-!~>])/g, '\\$1');

function mdInlineOut(nodes) {
  return (nodes || []).map((n) => {
    switch (n.type) {
      case 'text': return escapeMd(n.value);
      case 'break': return '  \n';
      case 'code': return `\`${n.value}\``;
      case 'strong': return `**${mdInlineOut(n.children)}**`;
      case 'em': return `*${mdInlineOut(n.children)}*`;
      // No Markdown underline exists; <u> is the documented fallback.
      case 'underline': return `<u>${mdInlineOut(n.children)}</u>`;
      case 'strike': return `~~${mdInlineOut(n.children)}~~`;
      case 'link': return `[${mdInlineOut(n.children)}](${n.href})`;
      default: return '';
    }
  }).join('');
}

function toMarkdown(blocks) {
  const parts = (blocks || []).map((b) => {
    switch (b.type) {
      case 'heading': return `${'#'.repeat(b.level)} ${mdInlineOut(b.children)}`;
      case 'thematicBreak': return '---';
      case 'codeBlock': return `\`\`\`${b.lang || ''}\n${b.value}\n\`\`\``;
      case 'blockquote':
        return toMarkdown(b.blocks).split('\n').map((l) => (l === '' ? '>' : `> ${l}`)).join('\n');
      case 'list':
        return b.items.map((item, idx) => {
          const marker = b.ordered ? `${idx + 1}.` : '-';
          const body = item.length === 1 && item[0].type === 'paragraph'
            ? mdInlineOut(item[0].children)
            : toMarkdown(item).replace(/\n/g, ' ');
          return `${marker} ${body}`;
        }).join('\n');
      default: return mdInlineOut(b.children);
    }
  });
  return parts.join('\n\n');
}

// ─── Emitter: plain text ─────────────────────────────────────────────────────

function toPlain(blocks) {
  const parts = (blocks || []).map((b) => {
    switch (b.type) {
      case 'thematicBreak': return '---';
      case 'codeBlock': return b.value;
      case 'blockquote': return toPlain(b.blocks);
      case 'list':
        return b.items.map((item, idx) => `${b.ordered ? `${idx + 1}.` : '-'} ${toPlain(item).replace(/\n+/g, ' ')}`).join('\n');
      default: return inlineText(b.children);
    }
  });
  return parts.join('\n\n');
}

// ─── Emitter: RTF ────────────────────────────────────────────────────────────

/**
 * Escape text for an RTF body: backslash and braces are structural, and any
 * character above ASCII must be emitted as a \uN escape with a fallback, since
 * the document declares a single-byte ANSI code page.
 */
function rtfEscape(s) {
  let out = '';
  for (const chr of String(s)) {
    const code = chr.codePointAt(0);
    if (chr === '\\' || chr === '{' || chr === '}') { out += `\\${chr}`; continue; }
    if (chr === '\n') { out += '\\line '; continue; }
    if (chr === '\t') { out += '\\tab '; continue; }
    if (code < 128) { out += chr; continue; }
    if (code > 0xffff) {
      // Astral plane: emit the UTF-16 surrogate pair, each as a signed word.
      const high = Math.floor((code - 0x10000) / 0x400) + 0xd800;
      const low = ((code - 0x10000) % 0x400) + 0xdc00;
      out += `\\u${high > 32767 ? high - 65536 : high}?\\u${low > 32767 ? low - 65536 : low}?`;
      continue;
    }
    out += `\\u${code > 32767 ? code - 65536 : code}?`;
  }
  return out;
}

function rtfInlineOut(nodes) {
  return (nodes || []).map((n) => {
    switch (n.type) {
      case 'text': return rtfEscape(n.value);
      case 'break': return '\\line ';
      case 'code': return `{\\f1 ${rtfEscape(n.value)}}`;
      case 'strong': return `{\\b ${rtfInlineOut(n.children)}}`;
      case 'em': return `{\\i ${rtfInlineOut(n.children)}}`;
      case 'underline': return `{\\ul ${rtfInlineOut(n.children)}}`;
      case 'strike': return `{\\strike ${rtfInlineOut(n.children)}}`;
      // RTF hyperlink fields are not universally honoured by readers, so the
      // URL is written inline where it is always visible.
      case 'link': return `${rtfInlineOut(n.children)} <${rtfEscape(n.href)}>`;
      default: return '';
    }
  }).join('');
}

// Half-point font sizes for h1..h6 in an RTF body whose default is 24 (12pt).
const RTF_HEADING_SIZE = [36, 32, 28, 26, 24, 22];

function rtfBody(blocks, indent = 0) {
  const pad = indent > 0 ? `\\li${indent * 360}` : '';
  return (blocks || []).map((b) => {
    switch (b.type) {
      case 'heading':
        return `{\\pard${pad}\\sa180\\b\\fs${RTF_HEADING_SIZE[b.level - 1] || 24} ${rtfInlineOut(b.children)}\\par}`;
      case 'thematicBreak':
        return `{\\pard${pad}\\brdrb\\brdrs\\brdrw10\\brsp20 \\par}`;
      case 'codeBlock':
        return `{\\pard${pad}\\f1\\sa180 ${rtfEscape(b.value)}\\par}`;
      case 'blockquote':
        return rtfBody(b.blocks, indent + 1);
      case 'list':
        return b.items.map((item, idx) => {
          const marker = b.ordered ? `${idx + 1}.` : '\\bullet';
          const body = item.map((blk) => (blk.children ? rtfInlineOut(blk.children) : rtfEscape(toPlain([blk])))).join(' ');
          return `{\\pard\\li${(indent + 1) * 360}\\fi-360 ${marker}\\tab ${body}\\par}`;
        }).join('\n');
      default:
        return `{\\pard${pad}\\sa180 ${rtfInlineOut(b.children)}\\par}`;
    }
  }).join('\n');
}

function toRtf(blocks) {
  const header = '{\\rtf1\\ansi\\ansicpg1252\\uc1\\deff0'
    + '{\\fonttbl{\\f0\\froman\\fcharset0 Times New Roman;}{\\f1\\fmodern\\fcharset0 Courier New;}}'
    + '\\fs24\n';
  return `${header}${rtfBody(blocks)}\n}`;
}

// ─── Format detection ────────────────────────────────────────────────────────

/**
 * Best-effort format detection, deliberately conservative: anything not clearly
 * RTF, HTML, or Markdown is treated as plain text. Detection is only ever a
 * default — the user (or the API caller) can always state the format
 * explicitly, and the CSV import lets the column be mapped directly.
 */
function detectFormat(input) {
  const src = String(input || '');
  if (src.trim() === '') { return 'plain'; }

  // RTF is unambiguous: the spec requires the document to open with {\rtf.
  if (/^\s*\{\\rtf\d/.test(src)) { return 'rtf'; }

  // HTML: a recognised tag, not merely an angle bracket. `a < b > c` and
  // `<3` must not trip this.
  if (/<(\/?)(p|div|span|br|hr|b|strong|i|em|u|s|del|ins|h[1-6]|ul|ol|li|blockquote|pre|code|a|table|tr|td|img|font)\b[^>]*>/i.test(src)) {
    return 'html';
  }

  // Markdown: require a structural construct at the start of a line, or a
  // paired inline marker. Bare '*' or '_' mid-sentence is not enough — an
  // measurement like "10 * 5 * 3 in" would otherwise be misread.
  const markdownSignals = [
    /^\s{0,3}#{1,6}\s+\S/m,          // heading
    /^\s{0,3}[-*+]\s+\S/m,           // bullet list
    /^\s{0,3}\d+[.)]\s+\S/m,         // numbered list
    /^\s{0,3}>\s+\S/m,               // blockquote
    /^\s*```/m,                      // fenced code
    /\*\*[^\s*][^*]*\*\*/,           // bold
    /~~[^\s~][^~]*~~/,               // strikethrough
    /\[[^\]]+\]\([^)\s]+\)/,         // link
    /^\s{0,3}([-*_]\s*){3,}$/m,      // thematic break
    /`[^`\n]+`/,                     // inline code
  ];
  if (markdownSignals.some((re) => re.test(src))) { return 'markdown'; }
  return 'plain';
}

// ─── Public API ──────────────────────────────────────────────────────────────

const PARSERS = { plain: parsePlain, markdown: parseMarkdown, html: parseHtml, rtf: parseRtf };
const EMITTERS = { plain: toPlain, markdown: toMarkdown, html: toHtml, rtf: toRtf };

const isFormat = (f) => FORMATS.includes(f);

/** Parse any supported format into the document AST. */
function parse(source, format) {
  const fmt = isFormat(format) ? format : detectFormat(source);
  return PARSERS[fmt](source);
}

/** Render the AST into any supported format. */
function render(blocks, format) {
  const fmt = isFormat(format) ? format : DEFAULT_FORMAT;
  return EMITTERS[fmt](blocks);
}

/**
 * Convert a description between formats. Converting to the same format is a
 * normalization pass, not a no-op — that is intentional, so stored values are
 * consistent regardless of how they were authored.
 */
function convert(source, from, to) {
  if (source === null || source === undefined || String(source).trim() === '') { return ''; }
  return render(parse(source, from), to);
}

/**
 * Derive everything the item record stores for a description. The source is
 * kept verbatim so editing is never lossy; html/text are derived for rendering,
 * search, and reports. Markdown and RTF are produced on demand at read time —
 * they are the rare requests and cost one parse each.
 */
function deriveDescription(source, format) {
  const raw = source === null || source === undefined ? '' : String(source);
  if (raw.trim() === '') {
    return { description: '', descriptionFormat: 'plain', descriptionHtml: '', descriptionText: '' };
  }
  const fmt = isFormat(format) ? format : detectFormat(raw);
  const ast = PARSERS[fmt](raw);
  return {
    description: raw,
    descriptionFormat: fmt,
    descriptionHtml: toHtml(ast),
    descriptionText: toPlain(ast),
  };
}

/**
 * Resolve the description an API response should carry, given the requested
 * format. 'source' returns exactly what the author typed.
 * Falls back to converting from the stored source when a derived field is
 * missing — records written before this feature have neither.
 */
function describeFor(item, requested) {
  const stored = item || {};
  const source = stored.description || '';
  const sourceFormat = isFormat(stored.descriptionFormat) ? stored.descriptionFormat : 'plain';
  if (!requested || requested === 'source') { return source; }
  if (!isFormat(requested)) { return source; }
  if (requested === sourceFormat) { return source; }
  if (requested === 'html' && typeof stored.descriptionHtml === 'string' && stored.descriptionHtml !== '') { return stored.descriptionHtml; }
  if (requested === 'plain' && typeof stored.descriptionText === 'string' && stored.descriptionText !== '') { return stored.descriptionText; }
  return convert(source, sourceFormat, requested);
}

export {
  FORMATS,
  DEFAULT_FORMAT,
  LIMITATIONS,
  SUPPORTED,
  ALLOWED_TAGS,
  detectFormat,
  parse,
  render,
  convert,
  deriveDescription,
  describeFor,
  isFormat,
  // Exported for tests and for callers that need a single direction.
  parsePlain, parseMarkdown, parseHtml, parseRtf,
  toPlain, toMarkdown, toHtml, toRtf,
  escapeHtml, decodeEntities, safeHref, rtfEscape, inlineText,
};
